import {
  Badge,
  EmptyState,
  FOCUS_RING,
  cn,
  type BadgeStatus,
  useLanguage,
  useTranslate,
} from '@athena/ui'
import { Link } from '@tanstack/react-router'
import { formatPercent, type Translate, type TranslationKey } from '@athena/i18n'
import type { ReactNode } from 'react'

import type { AttentionKind } from '../../graphql/generated/operations.js'
import { formatDate, relativeDay } from '../../lib/format.js'
import type { Language } from '../../lib/language.js'

export interface AttentionRow {
  id: string
  kind: AttentionKind
  studentId: string
  studentName: string
  practiceId: string
  practiceTitle: string
  attemptId: string | null
  percent: number | null
  occurredAt: string | null
}

export interface AttentionListProps {
  items: readonly AttentionRow[]
  /** How many the server found before it capped the list. */
  totalCount: number
  /** Injectable so "closes tomorrow" is testable rather than a fact about today. */
  now?: Date
}

/**
 * The badge each kind wears, and where the row leads.
 *
 * The pill is `Badge` rather than four class strings written out here. It used
 * to be the latter — borrowing Badge's colour pairs, whose contrast had been
 * settled once, but not the component — which left the dashboard with a pill
 * at a different padding from every other pill in the product, and with the
 * only four statuses that were a colour and a word and no glyph. The word and
 * the glyph now both come from the badge; what stays here is the routing,
 * which is this screen's business and not the design system's.
 */
const KIND = {
  CLOSING_SOON: { badge: 'closing', action: 'dashboard.attention.viewPractice' },
  LOW_SCORE: { badge: 'lowScore', action: 'dashboard.attention.viewAttempt' },
  IN_PROGRESS: { badge: 'inProgress', action: 'dashboard.attention.viewStudent' },
  NOT_STARTED: { badge: 'notStarted', action: 'dashboard.attention.viewStudent' },
} as const satisfies Record<AttentionKind, { badge: BadgeStatus; action: TranslationKey }>

/**
 * The detail line under the student's name: what is true, and when.
 *
 * The server sends a kind and a date; the wording is decided here, because
 * "closes tomorrow" is a rendering question that changes with the reader's
 * clock and their language, and the server has neither.
 */
function detailFor(row: AttentionRow, now: Date, t: Translate, language: Language): string {
  switch (row.kind) {
    case 'CLOSING_SOON':
      return row.occurredAt
        ? t('dashboard.attention.closingSoon', [
            row.practiceTitle,
            relativeDay(row.occurredAt, language, now),
          ])
        : row.practiceTitle
    // The score is not repeated here: it is the row's own figure, drawn
    // beside the badge, and saying it twice would bury it in a sentence.
    case 'LOW_SCORE':
      return row.practiceTitle
    case 'IN_PROGRESS':
      return row.occurredAt
        ? t('dashboard.attention.inProgress', [
            row.practiceTitle,
            formatDate(row.occurredAt, language),
          ])
        : row.practiceTitle
    case 'NOT_STARTED':
      return t('dashboard.attention.notStarted', [row.practiceTitle])
  }
}

/**
 * The one row worth clicking.
 *
 * Each kind leads to the screen that already answers it, rather than to a new
 * screen built for the dashboard: a deadline is a question about the practice,
 * a low score is a question about that attempt, and work sitting unfinished is
 * a question about the student.
 *
 * An attempt still in progress has no tutor review — its answers are not
 * written until submission — so those rows lead to the student, not the attempt.
 */
function RowLink({ row, children }: { row: AttentionRow; children: ReactNode }) {
  const language = useLanguage()
  const className = cn(
    // Two columns from `sm`: who and what on the left, taking every spare
    // pixel and wrapping a long practice title inside it; the result and the
    // action on the right, sized to their content. A long title then grows
    // the row downwards instead of pushing the result onto a line of its own.
    '-mx-2 flex min-w-0 cursor-pointer flex-col gap-3 rounded-sm px-2 py-4',
    'sm:grid sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-x-6',
    'transition-colors hover:bg-surface-secondary focus-visible:bg-surface-secondary',
    FOCUS_RING,
  )

  if (row.kind === 'CLOSING_SOON') {
    return (
      <Link
        to="/$language/practices/$practiceId"
        params={{ language, practiceId: row.practiceId }}
        className={className}
      >
        {children}
      </Link>
    )
  }

  if (row.kind === 'LOW_SCORE' && row.attemptId) {
    return (
      <Link
        to="/$language/attempts/$attemptId"
        params={{ language, attemptId: row.attemptId }}
        className={className}
      >
        {children}
      </Link>
    )
  }

  return (
    <Link
      to="/$language/students/$studentId"
      params={{ language, studentId: row.studentId }}
      className={className}
    >
      {children}
    </Link>
  )
}

/**
 * Needs attention: the first thing on the dashboard, and the only section that
 * is a list of things to do rather than a description of what happened.
 *
 * The rules that decide what belongs here are the server's, and so is the
 * order. Nothing is re-sorted or re-filtered here — a browser deciding which
 * students matter would be a second opinion nobody asked for, and would drift
 * from the one the server holds.
 */
export function AttentionList({ items, totalCount, now = new Date() }: AttentionListProps) {
  const t = useTranslate()
  const language = useLanguage()

  if (items.length === 0) {
    return (
      <EmptyState
        variant="section"
        title={t('dashboard.attention.emptyTitle')}
        description={t('dashboard.attention.emptyDescription')}
      />
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <ul className="divide-y divide-border">
        {items.map((row) => {
          const kind = KIND[row.kind]
          return (
            <li key={row.id}>
              <RowLink row={row}>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="text-body font-semibold text-text wrap-anywhere">
                    {row.studentName}
                  </span>
                  <span className="text-body-sm text-text-secondary wrap-anywhere">
                    {detailFor(row, now, t, language)}
                  </span>
                </span>

                <span className="flex flex-wrap items-center gap-x-4 gap-y-2 sm:flex-nowrap sm:justify-end">
                  {/* What tells one low score from the next is the score, so
                      it is the largest thing on the row and the badge beside
                      it only names the kind. Ordinary text colour: a score is
                      a figure to read, and the list is not an alarm. */}
                  {row.kind === 'LOW_SCORE' && row.percent !== null ? (
                    <span className="text-h3 font-bold tabular-nums text-text">
                      {formatPercent(row.percent, language)}
                    </span>
                  ) : null}
                  <Badge status={kind.badge} />
                  <span className="whitespace-nowrap text-body-sm font-semibold text-primary">
                    {t(kind.action)}
                    <span aria-hidden="true"> →</span>
                  </span>
                </span>
              </RowLink>
            </li>
          )
        })}
      </ul>

      {totalCount > items.length ? (
        <p className="text-body-sm text-text-secondary">
          {t('dashboard.attention.showingMostUrgent', [items.length, totalCount])}
        </p>
      ) : null}
    </div>
  )
}
